<template>
  <div class="container mx-auto">
    <h1 class="text-2xl font-bold mb-6">Document Upload</h1>

    <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
      <!-- Knowledgebase Upload Card -->
      <div class="bg-card rounded-lg shadow p-6">
        <h2 class="text-xl font-semibold mb-4">Upload to Knowledgebase</h2>
        <p class="text-muted-foreground mb-4">
          Upload documents to the Knowledgebase. These will be stored as OPERATIONS_MANUAL type documents.
        </p>

        <!-- Tab Navigation -->
        <div class="flex border-b border-gray-200 mb-6">
          <button v-if="hasPcoRole" @click="activeKnowledgebaseTab = 'pco'" :class="[
            'px-4 py-2 font-medium text-sm',
            activeKnowledgebaseTab === 'pco'
              ? 'border-b-2 border-primary text-primary'
              : 'text-gray-500 hover:text-gray-700'
          ]">
            PCO
          </button>
          <button @click="activeKnowledgebaseTab = 'planners'" :class="[
            'px-4 py-2 font-medium text-sm',
            activeKnowledgebaseTab === 'planners'
              ? 'border-b-2 border-primary text-primary'
              : 'text-gray-500 hover:text-gray-700'
          ]">
            Planners
          </button>
        </div>

        <!-- Knowledgebase Upload Form -->
        <form @submit.prevent="uploadToKnowledgebase" class="space-y-6 mb-6">
          <div>
            <label for="kb-file-upload" class="block text-sm font-medium mb-1">Select File</label>
            <input type="file" id="kb-file-upload" @change="handleKnowledgebaseFileChange"
              class="w-full px-3 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-primary">
          </div>

          <div>
            <label for="kb-file-name" class="block text-sm font-medium mb-1">File Name (Optional)</label>
            <input type="text" id="kb-file-name" v-model="knowledgebaseFileName"
              class="w-full px-3 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="Custom file name (leave empty to use original filename)">
          </div>

          <!-- Success and Error Messages -->
          <div v-if="knowledgebaseSuccessMessage || error" class="pt-4 border-t border-border">
            <div v-if="knowledgebaseSuccessMessage" class="p-3 bg-green-100 text-green-700 rounded-md mb-3">
              {{ knowledgebaseSuccessMessage }}
            </div>
            <div v-if="error" class="p-3 bg-red-100 text-red-700 rounded-md mb-3">
              {{ error.message }}
            </div>
          </div>

          <div class="flex justify-end space-x-3">
            <button type="button" class="px-4 py-2 border border-input rounded-md hover:bg-accent transition-colors"
              @click="resetKnowledgebaseForm">
              Cancel
            </button>
            <button type="submit"
              class="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
              :disabled="loading || !knowledgebaseSelectedFile">
              <span v-if="loading && activeUploadType === 'knowledgebase'">Uploading...</span>
              <span v-else>Upload to Knowledgebase</span>
            </button>
          </div>
        </form>

        <!-- Knowledgebase Files List -->
        <div class="mt-8 pt-6 border-t border-gray-200">
          <h3 class="text-lg font-medium mb-4">Knowledgebase Files</h3>

          <div v-if="loadingKnowledgebaseFiles" class="p-3 bg-blue-100 text-blue-700 rounded-md mb-3">
            Loading files...
          </div>

          <div v-else-if="filteredKnowledgebaseFiles.length === 0"
            class="p-3 bg-yellow-100 text-yellow-700 rounded-md mb-3">
            No files have been uploaded to Knowledgebase yet.
          </div>

          <div v-else>
            <!-- Always show first 3 files -->
            <ul class="divide-y divide-gray-200">
              <li v-for="file in filteredKnowledgebaseFiles.slice(0, 3)" :key="file.name" class="py-3">
                <div class="flex justify-between items-center">
                  <div>
                    <p class="font-medium">{{ file.name }}</p>
                    <p class="text-xs text-muted-foreground">
                      {{ formatFileSize(file.metadata?.size) }} •
                      {{ formatDate(file.created_at) }}
                    </p>
                  </div>
                  <div class="flex space-x-2">
                    <button @click="downloadFileByName(file.name, KNOWLEDGEBASE_BUCKET)"
                      class="px-3 py-1 bg-green-500 text-white text-sm rounded hover:bg-green-600"
                      title="Download file">
                      <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24"
                        stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                          d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                      </svg>
                    </button>
                    <button @click="confirmDelete(file.name, KNOWLEDGEBASE_BUCKET)"
                      class="px-3 py-1 bg-red-500 text-white text-sm rounded hover:bg-red-600" title="Delete file">
                      <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24"
                        stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                          d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </div>
              </li>
            </ul>

            <!-- Show more files if there are more than 3 -->
            <div v-if="filteredKnowledgebaseFiles.length > 3" class="mt-4">
              <!-- Additional files (collapsed by default) -->
              <ul v-if="showAllKnowledgebaseFiles" class="divide-y divide-gray-200 mt-3">
                <li v-for="file in filteredKnowledgebaseFiles.slice(3)" :key="file.name" class="py-3">
                  <div class="flex justify-between items-center">
                    <div>
                      <p class="font-medium">{{ file.name }}</p>
                      <p class="text-xs text-muted-foreground">
                        {{ formatFileSize(file.metadata?.size) }} •
                        {{ formatDate(file.created_at) }}
                      </p>
                    </div>
                    <div class="flex space-x-2">
                      <button @click="downloadFileByName(file.name, KNOWLEDGEBASE_BUCKET)"
                        class="px-3 py-1 bg-green-500 text-white text-sm rounded hover:bg-green-600"
                        title="Download file">
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24"
                          stroke="currentColor">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                            d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                        </svg>
                      </button>
                      <button @click="confirmDelete(file.name, KNOWLEDGEBASE_BUCKET)"
                        class="px-3 py-1 bg-red-500 text-white text-sm rounded hover:bg-red-600" title="Delete file">
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24"
                          stroke="currentColor">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </li>
              </ul>
              <button @click="showAllKnowledgebaseFiles = !showAllKnowledgebaseFiles"
                class="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors flex items-center justify-center">
                <span v-if="!showAllKnowledgebaseFiles">Show {{ filteredKnowledgebaseFiles.length - 3 }} more
                  files</span>
                <span v-else>Hide additional files</span>
              </button>

            </div>
          </div>

          <button v-if="knowledgebaseFiles.length > 0" @click="refreshKnowledgebaseFiles"
            class="mt-4 px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors w-full">
            Refresh List
          </button>
        </div>
      </div>

      <!-- Templates Upload Card -->
      <div class="bg-card rounded-lg shadow p-6">
        <h2 class="text-xl font-semibold mb-4">Upload to Templates</h2>
        <p class="text-muted-foreground mb-4">
          Upload documents to the Templates storage. These will be stored as SALES_REPORT type documents.
        </p>

        <!-- Templates Upload Form -->
        <form @submit.prevent="uploadToTemplates" class="space-y-6 mb-6">
          <div>
            <label for="tpl-file-upload" class="block text-sm font-medium mb-1">Select File</label>
            <input type="file" id="tpl-file-upload" @change="handleTemplatesFileChange"
              class="w-full px-3 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-primary">
          </div>

          <div>
            <label for="tpl-file-name" class="block text-sm font-medium mb-1">File Name (Optional)</label>
            <input type="text" id="tpl-file-name" v-model="templatesFileName"
              class="w-full px-3 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="Custom file name (leave empty to use original filename)">
          </div>

          <!-- Success and Error Messages -->
          <div v-if="templatesSuccessMessage || error" class="pt-4 border-t border-border">
            <div v-if="templatesSuccessMessage" class="p-3 bg-green-100 text-green-700 rounded-md mb-3">
              {{ templatesSuccessMessage }}
            </div>
            <div v-if="error" class="p-3 bg-red-100 text-red-700 rounded-md mb-3">
              {{ error.message }}
            </div>
          </div>

          <div class="flex justify-end space-x-3">
            <button type="button" class="px-4 py-2 border border-input rounded-md hover:bg-accent transition-colors"
              @click="resetTemplatesForm">
              Cancel
            </button>
            <button type="submit"
              class="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
              :disabled="loading || !templatesSelectedFile">
              <span v-if="loading && activeUploadType === 'templates'">Uploading...</span>
              <span v-else>Upload to Templates</span>
            </button>
          </div>
        </form>

        <!-- Templates Files List -->
        <div class="mt-8 pt-6 border-t border-gray-200">
          <h3 class="text-lg font-medium mb-4">Template Files</h3>

          <div v-if="loadingTemplatesFiles" class="p-3 bg-blue-100 text-blue-700 rounded-md mb-3">
            Loading files...
          </div>

          <div v-else-if="templatesFiles.length === 0" class="p-3 bg-yellow-100 text-yellow-700 rounded-md mb-3">
            No files have been uploaded to Templates yet.
          </div>

          <div v-else>
            <!-- Always show first 3 files -->
            <ul class="divide-y divide-gray-200">
              <li v-for="file in templatesFiles.slice(0, 3)" :key="file.name" class="py-3">
                <div class="flex justify-between items-center">
                  <div>
                    <p class="font-medium">{{ file.name }}</p>
                    <p class="text-xs text-muted-foreground">
                      {{ formatFileSize(file.metadata?.size) }} •
                      {{ formatDate(file.created_at) }}
                    </p>
                  </div>
                  <div class="flex space-x-2">
                    <button @click="downloadFileByName(file.name, TEMPLATES_BUCKET)"
                      class="px-3 py-1 bg-green-500 text-white text-sm rounded hover:bg-green-600"
                      title="Download file">
                      <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24"
                        stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                          d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                      </svg>
                    </button>
                    <button @click="confirmDelete(file.name, TEMPLATES_BUCKET)"
                      class="px-3 py-1 bg-red-500 text-white text-sm rounded hover:bg-red-600" title="Delete file">
                      <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24"
                        stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                          d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </div>
              </li>
            </ul>

            <!-- Show more files if there are more than 3 -->
            <div v-if="templatesFiles.length > 3" class="mt-4">
              <!-- Additional files (collapsed by default) -->
              <ul v-if="showAllTemplatesFiles" class="divide-y divide-gray-200 mt-3">
                <li v-for="file in templatesFiles.slice(3)" :key="file.name" class="py-3">
                  <div class="flex justify-between items-center">
                    <div>
                      <p class="font-medium">{{ file.name }}</p>
                      <p class="text-xs text-muted-foreground">
                        {{ formatFileSize(file.metadata?.size) }} •
                        {{ formatDate(file.created_at) }}
                      </p>
                    </div>
                    <div class="flex space-x-2">
                      <button @click="downloadFileByName(file.name, TEMPLATES_BUCKET)"
                        class="px-3 py-1 bg-green-500 text-white text-sm rounded hover:bg-green-600"
                        title="Download file">
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24"
                          stroke="currentColor">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                            d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                        </svg>
                      </button>
                      <button @click="confirmDelete(file.name, TEMPLATES_BUCKET)"
                        class="px-3 py-1 bg-red-500 text-white text-sm rounded hover:bg-red-600" title="Delete file">
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24"
                          stroke="currentColor">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </li>
              </ul>

              <button @click="showAllTemplatesFiles = !showAllTemplatesFiles"
                class="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors flex items-center justify-center">
                <span v-if="!showAllTemplatesFiles">Show {{ templatesFiles.length - 3 }} more files</span>
                <span v-else>Hide additional files</span>
              </button>
            </div>
          </div>

          <button v-if="templatesFiles.length > 0" @click="refreshTemplatesFiles"
            class="mt-4 px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors w-full">
            Refresh List
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted, watch } from 'vue';
import { useStorage } from '@/composables/useStorage';
import { useAuth } from '@/composables/useAuth';
import { logger, LogCategory } from '@/services/logger';
import { FileMetadata } from '@/types/storage';
import { BusinessRole } from '@/types/businessRole';
import { ExcelTemplateUploadService } from '@/services/excelTemplateUploadService';

export default defineComponent({
  name: 'KnowledgebaseUpload',
  setup() {
    const { loading, error, uploadFile: uploadStorageFile, listFiles: listStorageFiles, downloadFileByName, deleteFile: deleteStorageFile } = useStorage();
    const { checkAndRefreshAuth, profile } = useAuth();

    const excelTemplateUploadService = new ExcelTemplateUploadService();

    // Check if user has PCO role (HEAD_OF_PCO or CONTROLLER)
    const hasPcoRole = computed(() => {
      const userRole = profile.value?.business_role;
      return userRole === BusinessRole.HEAD_OF_PCO || userRole === BusinessRole.CONTROLLER;
    });

    // Knowledgebase form state
    const knowledgebaseSelectedFile = ref<File | null>(null);
    const knowledgebaseFileName = ref('');
    const knowledgebaseSuccessMessage = ref<string | null>(null);

    // Templates form state
    const templatesSelectedFile = ref<File | null>(null);
    const templatesFileName = ref('');
    const templatesSuccessMessage = ref<string | null>(null);

    // Shared state
    const files = ref<FileMetadata[]>([]);
    const loadingFiles = ref(false);
    const activeUploadType = ref<'knowledgebase' | 'templates' | null>(null);

    // Knowledgebase files state
    const knowledgebaseFiles = ref<FileMetadata[]>([]);
    const loadingKnowledgebaseFiles = ref(false);
    const showAllKnowledgebaseFiles = ref(false);

    // Templates files state
    const templatesFiles = ref<FileMetadata[]>([]);
    const loadingTemplatesFiles = ref(false);
    const showAllTemplatesFiles = ref(false);

    // Bucket names
    const KNOWLEDGEBASE_BUCKET = 'Knowledgebase';
    const TEMPLATES_BUCKET = 'Templates';

    // Add active tab state - default to planners
    const activeKnowledgebaseTab = ref<'pco' | 'planners'>('planners');

    // Watch for changes in hasPcoRole and reset tab if needed
    watch(hasPcoRole, (newHasPcoRole) => {
      if (!newHasPcoRole && activeKnowledgebaseTab.value === 'pco') {
        activeKnowledgebaseTab.value = 'planners';
      }
    });

    // Add computed property for filtered files
    const filteredKnowledgebaseFiles = computed(() => {
      return knowledgebaseFiles.value.filter(file => {
        if (activeKnowledgebaseTab.value === 'pco') {
          return file.only_pco_access === true;
        } else {
          return file.only_pco_access !== true;
        }
      });
    });

    // Computed property for total size of all files
    const totalSize = computed(() => {
      return files.value.reduce((total, file) => {
        return total + (file.metadata?.size || 0);
      }, 0);
    });

    // Knowledgebase file handling
    function handleKnowledgebaseFileChange(event: Event) {
      const input = event.target as HTMLInputElement;
      if (input.files && input.files.length > 0) {
        knowledgebaseSelectedFile.value = input.files[0];
        // Set the fileName to the original file name by default
        if (!knowledgebaseFileName.value) {
          knowledgebaseFileName.value = knowledgebaseSelectedFile.value.name;
        }
      }
    }

    function resetKnowledgebaseForm() {
      knowledgebaseSelectedFile.value = null;
      knowledgebaseFileName.value = '';
      knowledgebaseSuccessMessage.value = null;

      // Reset the file input
      const fileInput = document.getElementById('kb-file-upload') as HTMLInputElement;
      if (fileInput) fileInput.value = '';
    }

    // Templates file handling
    function handleTemplatesFileChange(event: Event) {
      const input = event.target as HTMLInputElement;
      if (input.files && input.files.length > 0) {
        templatesSelectedFile.value = input.files[0];
        // Set the fileName to the original file name by default
        if (!templatesFileName.value) {
          templatesFileName.value = templatesSelectedFile.value.name;
        }
      }
    }

    function resetTemplatesForm() {
      templatesSelectedFile.value = null;
      templatesFileName.value = '';
      templatesSuccessMessage.value = null;

      // Reset the file input
      const fileInput = document.getElementById('tpl-file-upload') as HTMLInputElement;
      if (fileInput) fileInput.value = '';
    }

    // Upload to Knowledgebase
    async function uploadToKnowledgebase() {
      if (!knowledgebaseSelectedFile.value) return;

      knowledgebaseSuccessMessage.value = null;
      activeUploadType.value = 'knowledgebase';

      try {
        // Check and refresh authentication if needed
        const isAuthenticated = await checkAndRefreshAuth();
        if (!isAuthenticated) {
          throw new Error('Authentication required');
        }

        // Generate a unique file path
        const timestamp = new Date().getTime();
        const finalFileName = knowledgebaseFileName.value || knowledgebaseSelectedFile.value.name;
        const filePath = `${timestamp}-${finalFileName}`;

        // Upload the file
        logger.info('Uploading file to Knowledgebase', LogCategory.STORAGE, {
          bucket: KNOWLEDGEBASE_BUCKET,
          fileName: filePath,
          originalName: knowledgebaseSelectedFile.value.name,
          size: knowledgebaseSelectedFile.value.size
        });
        const userId = profile.value?.id;
        const data = await uploadStorageFile(
          KNOWLEDGEBASE_BUCKET,
          filePath,
          knowledgebaseSelectedFile.value,
          {
            onlyPcoAccess: activeKnowledgebaseTab.value === 'pco',
            customFileName: finalFileName,
            userId: userId,
            createDocumentEntry: false // Skip document entry creation for Knowledgebase files
          }
        );

        knowledgebaseSuccessMessage.value = 'File uploaded successfully to Knowledgebase!';

        logger.info('File uploaded successfully to Knowledgebase', LogCategory.STORAGE, {
          bucket: KNOWLEDGEBASE_BUCKET,
          path: data.path || filePath
        });

        // Reset the form
        resetKnowledgebaseForm();

        // Refresh the Knowledgebase file list
        await refreshKnowledgebaseFiles();

        return data;
      } catch (err) {
        logger.error('File upload error', LogCategory.STORAGE, { error: err });
        error.value = err instanceof Error ? err : new Error(String(err));

        // Check if it's a bucket not found error
        if (error.value.message.includes('Bucket not found')) {
          error.value = new Error(`The ${KNOWLEDGEBASE_BUCKET} bucket does not exist. Please create it in the Supabase dashboard.`);
        }
      } finally {
        activeUploadType.value = null;
      }
    }

    // Upload to Templates
    async function uploadToTemplates() {
      if (!templatesSelectedFile.value) return;

      templatesSuccessMessage.value = null;
      activeUploadType.value = 'templates';

      try {
        const data = await excelTemplateUploadService.uploadTemplateFile(
          templatesFileName,
          templatesSelectedFile);

        templatesSuccessMessage.value = 'File uploaded successfully to Templates!';

        // Reset the form
        resetTemplatesForm();

        // Refresh the Templates file list
        await refreshTemplatesFiles();

        return data;
      } catch (err) {
        logger.error('File upload error', LogCategory.STORAGE, { error: err });
        error.value = err instanceof Error ? err : new Error(String(err));

        // Check if it's a bucket not found error
        if (error.value.message.includes('Bucket not found')) {
          error.value = new Error(`The Templates bucket does not exist. Please create it in the Supabase dashboard.`);
        }
      } finally {
        activeUploadType.value = null;
      }
    }

    // Refresh both file lists
    async function refreshFiles() {
      await Promise.all([
        refreshKnowledgebaseFiles(),
        refreshTemplatesFiles()
      ]);
    }

    // Refresh Knowledgebase files
    async function refreshKnowledgebaseFiles() {
      loadingKnowledgebaseFiles.value = true;

      try {
        // Check and refresh authentication if needed
        const isAuthenticated = await checkAndRefreshAuth();
        if (!isAuthenticated) {
          throw new Error('Authentication required');
        }

        logger.info(`Listing files in ${KNOWLEDGEBASE_BUCKET}`, LogCategory.STORAGE, { bucket: KNOWLEDGEBASE_BUCKET });

        const data = await listStorageFiles(KNOWLEDGEBASE_BUCKET);

        knowledgebaseFiles.value = data || [];

        logger.info(`Found ${knowledgebaseFiles.value.length} files in ${KNOWLEDGEBASE_BUCKET}`, LogCategory.STORAGE, {
          bucket: KNOWLEDGEBASE_BUCKET,
          count: knowledgebaseFiles.value.length
        });
      } catch (err) {
        logger.error('Failed to list Knowledgebase files', LogCategory.STORAGE, { error: err });
        error.value = err instanceof Error ? err : new Error(String(err));

        // Check if it's a bucket not found error
        if (error.value.message.includes('Bucket not found')) {
          error.value = new Error(`The ${KNOWLEDGEBASE_BUCKET} bucket does not exist. Please create it in the Supabase dashboard.`);
        }
      } finally {
        loadingKnowledgebaseFiles.value = false;
      }
    }

    // Refresh Templates files
    async function refreshTemplatesFiles() {
      loadingTemplatesFiles.value = true;

      try {
        // Check and refresh authentication if needed
        const isAuthenticated = await checkAndRefreshAuth();
        if (!isAuthenticated) {
          throw new Error('Authentication required');
        }

        logger.info(`Listing files in ${TEMPLATES_BUCKET}`, LogCategory.STORAGE, { bucket: TEMPLATES_BUCKET });

        const data = await listStorageFiles(TEMPLATES_BUCKET);

        templatesFiles.value = data || [];

        logger.info(`Found ${templatesFiles.value.length} files in ${TEMPLATES_BUCKET}`, LogCategory.STORAGE, {
          bucket: TEMPLATES_BUCKET,
          count: templatesFiles.value.length
        });
      } catch (err) {
        logger.error('Failed to list Templates files', LogCategory.STORAGE, { error: err });
        error.value = err instanceof Error ? err : new Error(String(err));

        // Check if it's a bucket not found error
        if (error.value.message.includes('Bucket not found')) {
          error.value = new Error(`The ${TEMPLATES_BUCKET} bucket does not exist. Please create it in the Supabase dashboard.`);
        }
      } finally {
        loadingTemplatesFiles.value = false;
      }
    }


    async function confirmDelete(fileName: string, bucket: string) {
      if (confirm(`Are you sure you want to delete ${fileName}?`)) {
        try {
          // Check and refresh authentication if needed
          const isAuthenticated = await checkAndRefreshAuth();
          if (!isAuthenticated) {
            throw new Error('Authentication required');
          }

          logger.info(`Deleting file from ${bucket}`, LogCategory.STORAGE, {
            bucket,
            fileName
          });

          await deleteStorageFile(bucket, fileName);

          logger.info('File deleted successfully', LogCategory.STORAGE, {
            bucket,
            fileName
          });

          // Refresh the appropriate file list
          if (bucket === KNOWLEDGEBASE_BUCKET) {
            await refreshKnowledgebaseFiles();
          } else if (bucket === TEMPLATES_BUCKET) {
            await refreshTemplatesFiles();
          }

          return true;
        } catch (err) {
          logger.error('Failed to delete file', LogCategory.STORAGE, { error: err });
          error.value = err instanceof Error ? err : new Error(String(err));
        }
      }
    }

    function formatFileSize(bytes?: number): string {
      if (!bytes) return '0 B';

      const units = ['B', 'KB', 'MB', 'GB', 'TB'];
      let size = bytes;
      let unitIndex = 0;

      while (size >= 1024 && unitIndex < units.length - 1) {
        size /= 1024;
        unitIndex++;
      }

      return `${size.toFixed(2)} ${units[unitIndex]}`;
    }

    function formatDate(dateString?: string): string {
      if (!dateString) return 'Unknown';

      const date = new Date(dateString);
      return date.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    }

    // Load files when component is mounted
    onMounted(async () => {
      try {
        logger.info('KnowledgebaseUpload component mounted, checking authentication', LogCategory.AUTH);
        await checkAndRefreshAuth();

        // Set the active tab based on user role
        if (!hasPcoRole.value) {
          activeKnowledgebaseTab.value = 'planners';
        }

        // Automatically list files when component is mounted
        await refreshFiles();
      } catch (err) {
        logger.error('Error checking authentication on mount', LogCategory.AUTH, { error: err });
      }
    });

    return {
      // Shared state
      loading,
      error,
      files,
      loadingFiles,
      totalSize,
      activeUploadType,

      // Knowledgebase files state
      knowledgebaseFiles,
      loadingKnowledgebaseFiles,
      refreshKnowledgebaseFiles,
      showAllKnowledgebaseFiles,

      // Templates files state
      templatesFiles,
      loadingTemplatesFiles,
      refreshTemplatesFiles,
      showAllTemplatesFiles,

      // Bucket constants
      KNOWLEDGEBASE_BUCKET,
      TEMPLATES_BUCKET,

      // Knowledgebase form
      knowledgebaseSelectedFile,
      knowledgebaseFileName,
      knowledgebaseSuccessMessage,
      handleKnowledgebaseFileChange,
      resetKnowledgebaseForm,
      uploadToKnowledgebase,

      // Templates form
      templatesSelectedFile,
      templatesFileName,
      templatesSuccessMessage,
      handleTemplatesFileChange,
      resetTemplatesForm,
      uploadToTemplates,

      // Shared methods
      refreshFiles,
      downloadFileByName,
      confirmDelete,
      formatFileSize,
      formatDate,

      // New state
      activeKnowledgebaseTab,
      filteredKnowledgebaseFiles,
      hasPcoRole,
    };
  }
});
</script>
